// src/pages/Dashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CollaboratorsManager from "../components/CollaboratorsManager";
import SharedAccessSection from "../components/SharedAccessSection";
import QuoteCard from "../components/QuoteCard";
import CollectionsSharedByMe from "@/components/CollectionsSharedByMe";
import SharedCollectionsList from "@/components/SharedCollectionsList";
import CollectionBreakdown from "../components/CollectionBreakdown";
import PersonalizedStartCard from "../components/PersonalizedStartCard";
import CollectorProfileCard from "../components/CollectorProfileCard";
import ClientMissionsCard from "../components/ClientMissionsCard";
import CollectionReadinessCard from "../components/CollectionReadinessCard";
import CollectionWatchlistCard from "../components/CollectionWatchlistCard";
import DossierSharesManager from "../components/DossierSharesManager";
import PresentationLinksManager from "../components/PresentationLinksManager";
import MessagesButton from "../components/MessagesButton";
import PendingExpertAccess from "@/components/PendingExpertAccess";
import MesDemandesCard from "@/components/dashboard/MesDemandesCard";
import BeneficiariesManagement from "../components/Profile/BeneficiariesManagement";
import { Shield, AlertTriangle, Search, BadgeCheck, Fingerprint, ScrollText, Check } from "lucide-react";

// ── Section « Actions » : repères visuels homogènes ──────────────────────────
// Pastille d'icône (repère) devant chaque carte + sur-titre de groupe doré.
const ICONCHIP_TONES = {
  ink: { bg: "#f5f3ee", fg: "#1a1a1a" },
  gold: { bg: "#faf6ec", fg: "#a9843f" },
  red: { bg: "#fdecec", fg: "#c0392b" },
};
const IconChip = ({ icon: Icon, tone = "ink" }) => {
  const s = ICONCHIP_TONES[tone] || ICONCHIP_TONES.ink;
  return (
    <span
      className="flex-shrink-0 inline-flex items-center justify-center rounded-xl"
      style={{ width: 40, height: 40, background: s.bg }}
      aria-hidden="true"
    >
      <Icon className="w-5 h-5" strokeWidth={1.75} style={{ color: s.fg }} />
    </span>
  );
};
const ActionEyebrow = ({ children }) => (
  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: "#a9843f" }}>
    {children}
  </p>
);

// Normalisation de nom d'artiste (accents, casse, espaces) pour la déduplication.
const normName = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toLowerCase();

// Imports avec gestion d'erreur
let OeuvresEnVente,
  ActualitesPropositions,
  ShareAccessModal,
  SharedDashboard;
try {
  OeuvresEnVente = require("../components/OeuvresEnVente").default;
} catch {}
try {
  ActualitesPropositions = require("../components/ActualitesPropositions").default;
} catch {}
try {
  ShareAccessModal = require("../components/ShareAccessModal").default;
} catch {}
try {
  SharedDashboard = require("../components/SharedDashboard").default; // eslint-disable-line no-unused-vars
} catch {}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-semibold">{this.props.message}</p>
          <p className="text-sm mt-1">{String(this.state.error)}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function EmptyState({ title, desc, primary, secondary }) {
  return (
    <div className="py-10 text-center">
      <div className="mx-auto max-w-md">
        <div className="text-lg font-semibold text-gray-900">{title}</div>
        {desc ? (
          <div className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</div>
        ) : null}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {primary?.to ? (
            <Link
              to={primary.to}
              className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
            >
              {primary.label}
            </Link>
          ) : (
            <button
              onClick={primary?.onClick}
              className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
              type="button"
            >
              {primary?.label}
            </button>
          )}

          {secondary?.to ? (
            <Link
              to={secondary.to}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              {secondary.label}
            </Link>
          ) : secondary?.onClick ? (
            <button
              onClick={secondary.onClick}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              type="button"
            >
              {secondary.label}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // ✅ IMPORTANT: un rôle "pro" (expert/partner/team/admin/family_office) ne doit jamais
  // être affecté par delegated_context (réservé aux satellites/collaborateurs).
  const roleFallback = user?.user_metadata?.role || user?.app_metadata?.role || "user";
  const isProUser = [
    "expert",
    "team",
    "admin",
    "partner",
    "partner_insurance",
    "partner_transport",
    "family_office",
  ].includes(roleFallback);

  // États existants
  const [delegatedCtx, setDelegatedCtx] = useState(null);
  const [, setDelegatedLoading] = useState(false);
  const [, setDelegatedError] = useState(null);
  const isDelegated = !!delegatedCtx?.owner_id;

  const [collections, setCollections] = useState([]);
  const [, setSharedCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [errorCollections, setErrorCollections] = useState(null);
  const [, setLoadingSharedCollections] = useState(false);
  const [, setErrorSharedCollections] = useState(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [accessDrawerOpen, setAccessDrawerOpen] = useState(false);
  const [activeSec, setActiveSec] = useState("sec-apercu"); // onglet sidebar actif
  // Desktop : une section à la fois (logique Origin). Mobile : tout affiché (pas de sidebar).
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else mq.removeListener(on);
    };
  }, []);
  const showSec = (id) => !isDesktop || activeSec === id;
  const [userProfile, setUserProfile] = useState(null);

  const [activeCollectionId, setActiveCollectionId] = useState(null);

  // ✅ NOUVEAU: on gèle l'id cible pour la modale (évite les changements en plein vol)
  const [shareTargetId, setShareTargetId] = useState(null);

  // ✅ helper : titre de la collection active (pour la modale)
  const activeCollectionTitle = useMemo(() => {
    if (!activeCollectionId) return "";
    const found = (collections || []).find((c) => c?.id === activeCollectionId);
    return found?.title || found?.name || "";
  }, [activeCollectionId, collections]);

  // ✅ helper : ouvre la modale en s'assurant qu'on a une collection active
  const openShareModal = () => {
    const targetId = activeCollectionId || collections?.[0]?.id || null;
    if (!targetId) return; // pas de collection, on ne monte pas la modale
    setShareTargetId(targetId);
    setShareOpen(true);
  };

  // ✅ CTA : trouver un expert (centre de gravité = /contacts)
  const goFindExpert = () => {
    navigate("/contacts");
  };

  // ✅ État pour les onglets News (Actualités/Galeries)
  const [marketPreviewTab, setMarketPreviewTab] = useState("auctions");
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Marché & actualités — SOURCE DE VÉRITÉ UNIQUE : RPC get_user_market_dashboard
  // (spec §4.4). Le serveur calcule les artistes surveillés (collection ∪ suivis,
  // dédupliqués) et sert ventes/expos. Phase 1 : pont via artist_alerts ; Phase 2
  // basculera sur market_items/user_market_items (crawler). Aucune donnée fictive.
  const [marketData, setMarketData] = useState(null);
  const fetchMarket = useCallback(async () => {
    const ownerId = isDelegated ? delegatedCtx?.owner_id : user?.id;
    if (!ownerId) { setMarketData(null); return; }
    try {
      const { data, error } = await supabase.rpc("get_user_market_dashboard", { p_profile_id: ownerId });
      if (error) throw error;
      setMarketData(data || null);
    } catch {
      setMarketData(null);
    }
  }, [isDelegated, delegatedCtx?.owner_id, user?.id]);
  useEffect(() => { fetchMarket(); }, [fetchMarket]);

  // Suivre / retirer la priorité d'un artiste (is_followed). Réservé au propriétaire :
  // la RLS artist_alerts exige profile_id = auth.uid() (un délégué ne peut pas écrire).
  // Les écritures restent directes sur la table ; on relit ensuite le RPC.
  const toggleFollow = async (artist) => {
    if (isDelegated || !user?.id || !artist) return;
    try {
      if (artist.alertId) {
        await supabase.from("artist_alerts").update({ is_followed: !artist.priority }).eq("id", artist.alertId);
      } else {
        await supabase.from("artist_alerts").insert({
          profile_id: user.id, artist_name: artist.name, is_in_collection: !!artist.collection,
          is_followed: true, is_active: true,
        });
      }
      await fetchMarket();
    } catch (e) { console.error("[market] toggleFollow", e); }
  };

  const addFollow = async () => {
    if (isDelegated || !user?.id) return;
    const name = (window.prompt(t("dashboard.market.followed.addPrompt", { defaultValue: "Nom de l'artiste à suivre :" })) || "").trim();
    if (!name) return;
    const existing = (marketData?.artists || []).find((a) => normName(a.name) === normName(name));
    try {
      if (existing && existing.alert_id) {
        if (!existing.priority) await supabase.from("artist_alerts").update({ is_followed: true }).eq("id", existing.alert_id);
      } else {
        await supabase.from("artist_alerts").insert({
          profile_id: user.id, artist_name: existing?.name || name,
          is_in_collection: !!existing?.collection, is_followed: true, is_active: true,
        });
      }
      await fetchMarket();
    } catch (e) { console.error("[market] addFollow", e); }
  };

  // Centres d'intérêt (brief §5) : rattachent foires/expos sans artiste identifié.
  // Écritures directes, RLS user_market_interests exige profile_id = auth.uid().
  const toggleInterest = async (interest) => {
    if (isDelegated || !user?.id || !interest) return;
    try {
      if (interest.selected) {
        await supabase.from("user_market_interests").delete().eq("profile_id", user.id).eq("interest_id", interest.id);
      } else {
        await supabase.from("user_market_interests").insert({ profile_id: user.id, interest_id: interest.id });
      }
      await fetchMarket();
    } catch (e) { console.error("[market] toggleInterest", e); }
  };

  const toggleShowMajorEvents = async () => {
    if (isDelegated || !user?.id) return;
    try {
      await supabase.from("user_market_preferences").upsert(
        { profile_id: user.id, show_major_events: !marketPreview.showMajorEvents, updated_at: new Date().toISOString() },
        { onConflict: "profile_id" },
      );
      await fetchMarket();
    } catch (e) { console.error("[market] toggleShowMajorEvents", e); }
  };

  // Mise en forme de la réponse RPC pour l'affichage (i18n des libellés, dates locales).
  const marketPreview = useMemo(() => {
    const d = marketData || {};
    const s = d.stats || {};
    const reasonText = (r, artistName) => {
      const base =
        r === "collection"
          ? t("dashboard.market.reason.collection", { defaultValue: "Artiste présent dans votre collection" })
          : r === "priority"
          ? t("dashboard.market.reason.priority", { defaultValue: "Artiste suivi en priorité" })
          : t("dashboard.market.reason.interest", { defaultValue: "Correspond à vos centres d'intérêt ou événement majeur du marché" });
      return artistName ? `${base} : ${artistName}` : base;
    };
    const fmt = (dt) => { try { return dt ? new Date(dt).toLocaleDateString(i18n.language || "fr") : ""; } catch { return dt || ""; } };

    const auctions = (d.auctions || []).map((a) => ({
      id: a.id, artist: a.artist, title: a.title, house: a.house || "", place: a.place || "",
      date: fmt(a.date), estimate: "", url: a.url || null, reason: reasonText(a.reason, a.artist),
    }));
    const news = (d.news || []).map((n) => ({
      id: n.id, source: n.source || n.artist, artist: n.artist,
      type: t("dashboard.market.type.article", { defaultValue: "Article" }),
      title: n.title, meta: [n.venue, fmt(n.starts_at)].filter(Boolean).join(" · "),
      url: n.url || null, reason: reasonText(n.reason, n.artist), unread: true,
    }));
    const eventTypeLabel = (itemType) =>
      itemType === "fair"
        ? t("dashboard.market.type.fair", { defaultValue: "Foire" })
        : t("dashboard.market.type.exhibition", { defaultValue: "Exposition" });
    const events = (d.events || []).map((ev) => ({
      id: ev.id, source: ev.source || ev.artist, artist: ev.artist,
      type: eventTypeLabel(ev.item_type),
      title: ev.title,
      meta: [ev.venue, [fmt(ev.starts_at), fmt(ev.ends_at)].filter(Boolean).join(" – ")].filter(Boolean).join(" · "),
      url: ev.url || null, reason: reasonText(ev.reason, ev.artist), unread: true,
    }));
    const artists = (d.artists || []).map((a, i) => ({
      id: "art-" + i, name: a.name, collection: !!a.collection, priority: !!a.priority, alertId: a.alert_id || null,
    }));
    const interestLabels = {
      "art-moderne": "Art moderne", "art-contemporain": "Art contemporain",
      "photographie": "Photographie", "design": "Design",
      "art-numerique": "Art numérique / nouveaux médias", "arts-asie": "Arts d'Asie",
      "arts-afrique": "Arts d'Afrique", "art-ancien": "Art ancien",
      "mobilier-objets-art": "Mobilier et objets d'art", "joaillerie": "Joaillerie",
      "livres-manuscrits": "Livres et manuscrits",
    };
    const interests = (d.interests || []).map((it) => ({
      id: it.id, slug: it.slug,
      label: t(it.label_i18n_key, { defaultValue: interestLabels[it.slug] || it.slug }),
      selected: !!it.selected,
    }));

    return {
      stats: {
        watchedArtists: s.watched_artists ?? artists.length,
        matchingAuctions: s.matching_auctions ?? auctions.length,
        unreadNews: s.unread_news ?? news.length,
        upcomingEvents: s.upcoming_events ?? events.length,
      },
      auctions, news, events, artists, interests,
      showMajorEvents: d.show_major_events ?? true,
    };
  }, [marketData, t, i18n]);

  // ✅ États pour le résumé des contacts
  const [contactsSummary, setContactsSummary] = useState({
    total: 0,
    advisors: 0,
    experts: 0,
    insurers: 0,
  });
  const [loadingContacts, setLoadingContacts] = useState(false);

  // États assurance
  const [insSummary, setInsSummary] = useState({
    total: 0,
    pending: 0,
    seen: 0,
    answered: 0,
    latest: null,
  });
  const [insSummaryLoading, setInsSummaryLoading] = useState(false);
  const [insSummaryError, setInsSummaryError] = useState(null);

  const totalCollections = collections?.length || 0;
  const totalArtworks = useMemo(() => {
    try {
      return (collections || []).reduce(
        (sum, c) => sum + ((c?.artworks || []).length || 0),
        0
      );
    } catch {
      return 0;
    }
  }, [collections]);

  const AccessPanel = () => (
    <div className="space-y-6">
      {/* Réseau d'experts Artkiv — rien ici ne menait vers /experts avant :
          on ne pouvait découvrir de nouveaux experts qu'en tombant dessus
          par une autre page de services. Retour terrain 11/09/2026. */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-base font-semibold text-gray-900">Réseau d'experts Artkiv</div>
          <div className="text-sm text-gray-500 mt-1">
            Parcourez l'annuaire des experts (conservateurs, commissaires-priseurs, assureurs…), partagez l'accès à votre collection, et validez ceux en qui vous avez confiance.
          </div>
        </div>
        <Link
          to="/experts"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800"
        >
          Voir l'annuaire →
        </Link>
      </div>

      {/* 🆕 Experts en attente de partage */}
      {!isDelegated && user ? (
        <div>
          <ErrorBoundary message={t("dashboard.errors.module")}>
            <PendingExpertAccess clientId={user.id} />
          </ErrorBoundary>
        </div>
      ) : null}

      {/* ✅ Collaborators */}
      {!isDelegated && user ? (
        <div>
          <ErrorBoundary message={t("dashboard.errors.module")}>
            <CollaboratorsManager
              userId={user.id}
              userPlan={userProfile?.plan || "premium"}
              collectionId={null}
            />
          </ErrorBoundary>
        </div>
      ) : null}

      {/* ⭐ Section Experts Partagés - fonctionne avec ou sans collectionId */}
      {!isDelegated && user ? (
        <div>
          <ErrorBoundary message={t("dashboard.errors.module")}>
            <SharedAccessSection collectionId={null} />
          </ErrorBoundary>
        </div>
      ) : null}
    </div>
  );

  const Drawer = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50">
        {/* overlay */}
        <button
          type="button"
          aria-label={t("common.close")}
          onClick={onClose}
          className="absolute inset-0 bg-black/40"
        />

        {/* panel */}
        <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl">
          <div className="h-full flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="text-base font-semibold text-gray-900">{title}</div>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 hover:bg-gray-50"
              >
                {t("common.close", { defaultValue: "Fermer" })}
              </button>
            </div>
            <div className="p-5 overflow-auto">{children}</div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ Fonction pour récupérer le profil utilisateur
  const fetchUserProfile = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("plan, first_name,last_name")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (err) {
      console.error("Erreur fetchUserProfile:", err);
    }
  };

  // ✅ Fonction pour valider l'accès délégué
  const validateDelegatedAccess = async () => {
    if (!delegatedCtx?.collaborator_id || !delegatedCtx?.owner_id) return;
    try {
      setDelegatedLoading(true);
      const { data, error } = await supabase
        .from("team_members")
        .select("role, can_view, can_edit")
        .eq("id", delegatedCtx.collaborator_id)
        .eq("client_id", delegatedCtx.owner_id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setDelegatedError(t("dashboard.errors.delegatedMissing"));
      } else if (!data.can_view) {
        setDelegatedError(t("dashboard.errors.delegatedForbidden"));
      }
    } catch (err) {
      console.error("Erreur validation accès délégué:", err);
      setDelegatedError(err.message);
    } finally {
      setDelegatedLoading(false);
    }
  };

  // Fonction pour récupérer les collections
  const fetchCollections = async () => {
    if (!user?.id) return;

    const effectiveUserId = isDelegated ? delegatedCtx.owner_id : user.id;

    try {
      setLoadingCollections(true);
      setErrorCollections(null);

      const { data, error } = await supabase
        .from("collections")
        .select("*, artworks(*)")
        .eq("client_id", effectiveUserId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setCollections(data || []);

      // ✅ FIX: ne pas écraser activeCollectionId si déjà définie
      setActiveCollectionId((prev) => {
        if (prev) return prev;
        return data?.[0]?.id || null;
      });
    } catch (err) {
      console.error("Erreur fetchCollections:", err);
      setErrorCollections(err.message || t("dashboard.errors.collectionsLoad"));
    } finally {
      setLoadingCollections(false);
    }
  };

  // Fonction pour récupérer le résumé des demandes d'assurance
  const fetchInsuranceSummary = async () => {
    if (!user?.id) return;
    const effectiveUserId = isDelegated ? delegatedCtx.owner_id : user.id;

    try {
      setInsSummaryLoading(true);
      setInsSummaryError(null);

      const { data, error } = await supabase
        .from("insurance_requests")
        .select("*")
        .eq("created_by", effectiveUserId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const all = data || [];
      const pending = all.filter((r) => r.status === "pending").length;
      const seen = all.filter((r) => r.status === "seen").length;
      const answered = all.filter((r) => r.status === "answered").length;
      const latest = all[0] || null;

      setInsSummary({
        total: all.length,
        pending,
        seen,
        answered,
        latest,
      });
    } catch (err) {
      console.error("Erreur fetchInsuranceSummary:", err);
      setInsSummaryError(err.message);
    } finally {
      setInsSummaryLoading(false);
    }
  };

  // Fonction pour récupérer les collections partagées
  const fetchSharedCollections = async () => {
    if (!user?.id || isDelegated) return;

    try {
      setLoadingSharedCollections(true);
      setErrorSharedCollections(null);

      const { data: perms, error: permsError } = await supabase
        .from("collection_permissions")
        .select("collection_id, client_id, can_view, can_edit")
        .eq("grantee_id", user.id)
        .eq("can_view", true);

      if (permsError) throw permsError;

      const permRows = Array.isArray(perms) ? perms : [];
      if (permRows.length === 0) {
        setSharedCollections([]);
        return;
      }

      const specificRows = permRows.filter((p) => !!p.collection_id);
      const specificIds = specificRows.map((p) => p.collection_id);

      const globalRows = permRows.filter((p) => !p.collection_id);
      const globalClientIds = [
        ...new Set(globalRows.map((p) => p.client_id).filter(Boolean)),
      ];

      let allCollections = [];

      if (specificIds.length > 0) {
        const { data: byId, error: byIdError } = await supabase
          .from("collections")
          .select("*, artworks(*)")
          .in("id", specificIds);
        if (byIdError) throw byIdError;
        allCollections = allCollections.concat(byId || []);
      }

      if (globalClientIds.length > 0) {
        const { data: byClient, error: byClientError } = await supabase
          .from("collections")
          .select("*, artworks(*)")
          .in("client_id", globalClientIds);
        if (byClientError) throw byClientError;
        allCollections = allCollections.concat(byClient || []);
      }

      const uniqueIds = new Set();
      const deduped = allCollections.filter((c) => {
        if (uniqueIds.has(c.id)) return false;
        uniqueIds.add(c.id);
        return true;
      });

      setSharedCollections(deduped);
    } catch (err) {
      console.error("Erreur fetchSharedCollections:", err);
      setErrorSharedCollections(
        err.message || t("dashboard.errors.sharedCollectionsLoad")
      );
    } finally {
      setLoadingSharedCollections(false);
    }
  };

  // ✅ Fonction pour récupérer le résumé des contacts
  // Lit les accès réellement accordés (collection_permissions), pas les tables
  // expert_clients/preferred_experts/preferred_insurers — legacy, jamais
  // synchronisées avec les vraies permissions (piège déjà documenté : voir
  // "Fix onglet Clients Advisor" — expert_clients vs advisor_clients).
  // Catégorisation (cf. ARTKIV_DISTINCTION_EXPERT_VALORISATION_ART_ADVISOR) :
  //   - Conseiller (Art Advisor)   → profiles.plan in (art_advisor, art_advisor_premium)
  //   - Expert en valorisation     → profiles.partner_type = 'expert_valuation'
  //   - Assureur                  → profiles.partner_type = 'insurance'
  const fetchContactsSummary = async () => {
    if (!user?.id) return;

    try {
      setLoadingContacts(true);

      // RPC SECURITY DEFINER : la RLS profiles n'autorise pas un client à lire
      // les profils de ses grantees (can_read_profile ne marche que dans le
      // sens grantee → client), donc le calcul se fait côté serveur.
      const { data, error } = await supabase.rpc("get_my_contacts_summary").maybeSingle();
      if (error) throw error;

      setContactsSummary({
        total: data?.total || 0,
        advisors: data?.advisors || 0,
        experts: data?.experts || 0,
        insurers: data?.insurers || 0,
      });
    } catch (err) {
      console.error("Erreur fetchContactsSummary:", err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // useEffect principal
  useEffect(() => {
    if (user?.id) {
      if (!isProUser && delegatedCtx?.collaborator_id) {
        validateDelegatedAccess();
      }
      fetchUserProfile();
      fetchCollections();
      if (!isDelegated) fetchSharedCollections();
      fetchInsuranceSummary();
      fetchContactsSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    delegatedCtx?.collaborator_id,
    delegatedCtx?.owner_id,
    delegatedCtx?.collection_id,
  ]);

  // Récupération du contexte délégué au montage
  useEffect(() => {
    if (isProUser) {
      try {
        localStorage.removeItem("delegated_context");
      } catch {}
      return;
    }

    const stored = localStorage.getItem("delegated_context");
    if (stored) {
      try {
        const ctx = JSON.parse(stored);
        setDelegatedCtx(ctx);
      } catch (err) {
        console.error("Erreur parsing delegated_context:", err);
      }
    }
  }, [isProUser]);

  console.log("[Dashboard] roleFallback:", roleFallback, "isProUser:", isProUser);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:flex lg:gap-8">
        {/* Menu latéral collectionneur — navigation par section (comme l'advisor) */}
        {!isDelegated && (
          <aside className="hidden lg:block w-56 shrink-0">
            <nav className="sticky top-24">
              <p className="px-3 mb-3 text-[0.7rem] tracking-[0.14em] uppercase" style={{ color: "#b8b1a4" }}>
                {t("dashboard.navigation.sectionsLabel", { defaultValue: "Sections" })}
              </p>
              <div className="space-y-0.5">
                {[
                  {
                    id: "sec-apercu",
                    label: t("dashboard.navigation.overview"),
                    hint: t("dashboard.navigation.overviewHint", {
                      defaultValue: "Where does my collection stand?",
                    }),
                    icon: '<path d="M3 9.5 12 3l9 6.5"/><path d="M5 8.5V21h14V8.5"/>',
                  },
                  {
                    id: "sec-collection",
                    label: t("dashboard.navigation.collection"),
                    hint: t("dashboard.navigation.collectionHint", {
                      defaultValue: "What do I own?",
                    }),
                    icon: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 15l5-5 4 4 3-3 6 6"/><circle cx="8.5" cy="8.5" r="1.4"/>',
                  },
                  {
                    id: "sec-acces",
                    label: t("dashboard.navigation.contactsAccess"),
                    hint: t("dashboard.navigation.contactsAccessHint", {
                      defaultValue: "Who do I work with, and who can see what?",
                    }),
                    icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="3.2"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
                  },
                  {
                    id: "sec-marche",
                    label: t("dashboard.navigation.marketNews"),
                    hint: t("dashboard.navigation.marketNewsHint", {
                      defaultValue: "What is happening around my artists and the market?",
                    }),
                    icon: '<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>',
                  },
                  {
                    id: "sec-propositions",
                    label: t("dashboard.navigation.actions", { defaultValue: "Actions" }),
                    hint: t("dashboard.navigation.actionsHint", {
                      defaultValue: "What do I want to do with my artworks?",
                    }),
                    icon: '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z"/>',
                  },
                ].map(({ id: secId, label, hint, icon }) => {
                  const active = activeSec === secId;
                  return (
                    <button
                      key={secId}
                      type="button"
                      onClick={() => {
                        setActiveSec(secId);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
                      style={active ? { background: "#faf6ec", color: "#1a1a1a" } : { color: "#6b6459" }}
                    >
                      <svg
                        width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke={active ? "#a9843f" : "#b8b1a4"} strokeWidth="1.7"
                        strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0"
                        dangerouslySetInnerHTML={{ __html: icon }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm ${active ? "font-medium" : ""}`}>
                          {label}
                        </span>
                        <span
                          className="mt-0.5 block text-[11px] leading-4"
                          style={{ color: active ? "#8a7a52" : "#aaa296" }}
                        >
                          {hint}
                        </span>
                      </span>
                      {active && <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: "#c9a961" }} />}
                    </button>
                  );
                })}
              </div>
            </nav>
          </aside>
        )}
        <div className="min-w-0 flex-1">
        <div id="sec-apercu" className="scroll-mt-24" />
        {showSec("sec-apercu") && (<>
        {/* §4 onboarding — personnalisation (raccourcis/conseil selon le profil).
            Le socle ci-dessous reste identique ; masquable par l'utilisateur. */}
        {!isDelegated && <PersonalizedStartCard />}
        {!isDelegated && <CollectorProfileCard />}
        {/* État patrimonial / Collection Readiness (§9.1) — moteur commun readiness.js */}
        <CollectionReadinessCard userId={isDelegated ? delegatedCtx?.owner_id : user?.id} />
        <CollectionWatchlistCard userId={isDelegated ? delegatedCtx?.owner_id : user?.id} />
        {!isDelegated && <ClientMissionsCard />}
        </>)}
        <div id="sec-acces" className="scroll-mt-24" />
        {showSec("sec-acces") && (<>
        {/* ✅ Layout Grid: QuoteCard à gauche (desktop) / en haut (mobile) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
          {/* QuoteCard - Colonne gauche sur desktop */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-8">
              {/* Pas de filtre de topics : le tirage se fait parmi toutes les citations éligibles
                  (la RPC ne choisit désormais un topic que parmi ceux qui ont une citation pour
                  cette page/langue — migration du 08/10/2026). Avant, topics=["collection"]
                  ne laissait que 2 citations FR → toujours la même.
                  cooldownHours=0 : pas d'exclusion des citations déjà vues (pool réduit). */}
              <QuoteCard
                pageKey="collector.dashboard"
                context="dashboard"
                cooldownHours={0}
                variant="postit"
                lang={(i18n.resolvedLanguage || i18n.language || "fr").split("-")[0]}
              />
            </div>
          </div>

          {/* Contenu principal - Colonne droite sur desktop */}
          <div className="lg:col-span-8 space-y-10">
            {/* ✅ Section Contacts - Design élégant */}
            {!isDelegated && (
              <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 hover:border-gray-200 hover:shadow-sm transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                  <div className="flex-1">
                    <h2
                      className="text-2xl md:text-3xl font-light text-gray-900 mb-2"
                      style={{ fontFamily: "Georgia, serif" }}
                    >
                      {t("contacts.title")}
                    </h2>
                    <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                      {t("contacts.subtitle")}
                    </p>
                  </div>
                  <Link
                    to="/contacts"
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm font-medium rounded-xl hover:bg-gray-900 transition-all duration-200 hover:shadow-lg self-start md:self-auto"
                  >
                    <span>{t("contacts.viewAll")}</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </Link>
                </div>

                {loadingContacts ? (
                  <div className="text-center py-16 text-gray-400">
                    <div className="inline-block animate-pulse text-sm">{t("contacts.loading")}</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {[
                      { value: contactsSummary.total, label: t("contacts.stats.total"),
                        icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="3.2"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>' },
                      { value: contactsSummary.advisors, label: t("contacts.stats.advisors"),
                        icon: '<circle cx="12" cy="12" r="9"/><circle cx="8" cy="10" r="1"/><circle cx="12" cy="8" r="1"/><circle cx="16" cy="10" r="1"/><path d="M12 21a2 2 0 0 1-.5-3.9c.8-.2 1.3-1 1-1.8-.2-.6.1-1.3.8-1.3H16a5 5 0 0 0 0-8"/>' },
                      { value: contactsSummary.experts, label: t("contacts.stats.experts"),
                        icon: '<path d="M12 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8"/><path d="M8.2 13.5 7 22l5-3 5 3-1.2-8.5"/>' },
                      { value: contactsSummary.insurers, label: t("contacts.stats.insurers"),
                        icon: '<path d="M12 3l7 2.5v5.2c0 4.3-3 7.4-7 9.3-4-1.9-7-5-7-9.3V5.5z"/><path d="M9.3 12l1.8 1.8L15 10"/>' },
                    ].map((stat, idx) => (
                      <div
                        key={idx}
                        className="group relative rounded-2xl p-5 md:p-6 transition-all duration-300 hover:shadow-md"
                        style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}
                      >
                        <svg className="absolute top-4 right-4" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c9a961" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }} dangerouslySetInnerHTML={{ __html: stat.icon }} />
                        <div className="text-3xl md:text-4xl font-light text-gray-900 mb-2">
                          {stat.value}
                        </div>
                        <div className="text-xs uppercase tracking-wide font-medium" style={{ color: "#8a7a52" }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ✅ Section Accès & Collaboration */}
            {!isDelegated && user ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 hover:border-gray-200 hover:shadow-sm transition-all duration-300">
                {/* En-tête pleine largeur : le titre a de l'air, ne casse plus */}
                <div className="mb-6 max-w-2xl">
                  <h2
                    className="text-2xl md:text-3xl font-light text-gray-900 mb-3"
                    style={{ fontFamily: "Georgia, serif" }}
                  >
                    {t("dashboard.access.title", { defaultValue: "Accès & collaboration" })}
                  </h2>
                  <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-2">
                    {t("dashboard.access.desc", {
                      defaultValue:
                        "Invitez vos collaborateurs, suivez les accès et partagez des œuvres avec vos experts, au même endroit.",
                    })}
                  </p>
                  <p className="text-sm text-gray-400 italic">
                    {t("dashboard.access.hint", {
                      defaultValue: "Partagez vos œuvres avec qui vous voulez, quand vous voulez.",
                    })}
                  </p>
                </div>

                {/* CTAs desktop — rangée sous l'en-tête, passe à la ligne ; un seul
                    primaire (or), un secondaire (contour), un tertiaire (crème) */}
                <div className="hidden md:flex flex-wrap items-center gap-3">
                  <MessagesButton />
                  <button
                    type="button"
                    onClick={openShareModal}
                    className="px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:shadow-lg"
                    style={{ background: "#c9a961", color: "#1a1a1a" }}
                  >
                    {t("dashboard.access.ctaShareAccess", { defaultValue: "Partager l’accès" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccessDrawerOpen(true)}
                    className="px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                    style={{ background: "#f4f1ea", color: "#1a1a1a" }}
                  >
                    {t("dashboard.access.ctaManage", { defaultValue: "Gérer" })}
                  </button>
                </div>

                {/* Mobile accordion */}
                <div className="md:hidden mt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="sm:col-span-2 flex justify-center">
                      <MessagesButton />
                    </div>
                    <button
                      type="button"
                      onClick={openShareModal}
                      className="px-5 py-3 rounded-xl text-sm font-medium hover:shadow-lg transition-all duration-200"
                      style={{ background: "#c9a961", color: "#1a1a1a" }}
                    >
                      {t("dashboard.access.ctaShareAccess", { defaultValue: "Partager l’accès" })}
                    </button>
                  </div>
                  <details className="group">
                    <summary className="list-none cursor-pointer select-none flex items-center justify-between px-5 py-4 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-gray-300 transition-all duration-200">
                      <span className="text-sm font-medium text-gray-900">
                        {t("dashboard.access.mobileSummary", { defaultValue: "Voir / gérer" })}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {t("dashboard.access.tap", { defaultValue: "Toucher pour ouvrir" })}
                        </span>
                        <span className="text-gray-500 group-open:rotate-180 transition-transform">
                          ▼
                        </span>
                      </div>
                    </summary>
                    <div className="pt-6 px-2">
                      <AccessPanel />
                    </div>
                  </details>
                </div>

              </div>
            ) : null}
          </div>
        </div>

        </>)}
        {/* Drawer (desktop) */}
        <Drawer
          open={accessDrawerOpen}
          onClose={() => setAccessDrawerOpen(false)}
          title={t("dashboard.access.drawerTitle", { defaultValue: "Accès & collaboration" })}
        >
          <AccessPanel />
        </Drawer>

        <div id="sec-collection" className="scroll-mt-24" />
        {showSec("sec-collection") && (<>
        {/* Mes collections */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-light text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
              {t("dashboard.works")}
            </h2>
          </div>

          {loadingCollections ? (
            <div className="py-10 text-center text-gray-500">{t("dashboard.loading.collection")}</div>
          ) : errorCollections ? (
            <div className="py-10 text-center text-red-600">{errorCollections}</div>
          ) : collections.length === 0 ? (
            <EmptyState
              title={t("dashboard.emptyWorks.title")}
              desc={t("dashboard.emptyWorks.desc")}
              primary={{ label: t("dashboard.emptyWorks.ctaPrimary"), to: "/add" }}
              secondary={{ label: t("dashboard.emptyWorks.ctaSecondary"), onClick: openShareModal }}
            />
          ) : (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="text-sm text-gray-600">
                    {t("dashboard.collectionSummary.summary", { defaultValue: "Résumé de votre collection" })}
                  </div>

                  <div className="mt-1 text-base font-semibold text-gray-900">
                    {totalArtworks.toLocaleString(i18n.resolvedLanguage || i18n.language)}{" "}
                    {t("dashboard.collectionSummary.artworks", { defaultValue: "œuvres" })} •{" "}
                    {totalCollections.toLocaleString(i18n.resolvedLanguage || i18n.language)}{" "}
                    {t("dashboard.collectionSummary.collections", { defaultValue: "collections" })}
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    {t("dashboard.collectionSummary.note", {
                      defaultValue:
                        "Pour garder un dashboard lisible, l'affichage complet est dans \"Collections\" (menu).",
                    })}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Link
                    to="/add"
                    className="px-4 py-2 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900 text-center"
                  >
                    {t("dashboard.collectionSummary.ctaAdd", { defaultValue: "Ajouter une œuvre" })}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        </>)}
        <div id="sec-partages" className="scroll-mt-24" />
        {/* Regroupé dans « Contacts & accès » : gestion des partages/accès */}
        {showSec("sec-acces") && (<>
        {/* Collections que j'ai partagées */}
        {!isDelegated && (
          <CollectionsSharedByMe
            className="mb-10"
            onOpenShareModal={openShareModal}
            onManageAccess={() => setAccessDrawerOpen(true)}
            onFindExpert={() => navigate("/contacts")}
          />
        )}

        {/* Collections que d'autres ont partagées AVEC moi (accès reçu) */}
        {!isDelegated && (
          <div className="mb-10">
            <SharedCollectionsList />
          </div>
        )}

        {/* Partages de dossiers institutionnels (Phase 3) — gérer/révoquer, audit */}
        {!isDelegated && <DossierSharesManager />}

        {/* Liens de présentation (sans compte) — journal des consultations, révocation */}
        {!isDelegated && <PresentationLinksManager />}

        {/* Demandes + Gestion des héritiers (rapatriés du profil : ce sont des
            interlocuteurs/accès de transmission, ils vivent mieux ici). */}
        {!isProUser && !isDelegated && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10 items-start">
            <MesDemandesCard />
            <BeneficiariesManagement />
          </div>
        )}
        </>)}
        {/* Positionnement + œuvres en vente : rattachés à l'onglet « Collection » */}
        {showSec("sec-collection") && (<>

        {/* ── Positionnement de la collection ── */}
        {!isDelegated && totalArtworks > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2
                  className="text-2xl font-light text-gray-900"
                  style={{ fontFamily: "Georgia, serif" }}
                >
                  {t("dashboard.breakdown.title", { defaultValue: "Positionnement de la collection" })}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t("dashboard.breakdown.subtitle", {
                    defaultValue: "Répartition par médium, type, période et lieu",
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                className="self-start sm:self-auto flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-medium border transition-all duration-200"
                style={
                  showBreakdown
                    ? { background: "#1a1a1a", color: "#fff", border: "1px solid #1a1a1a" }
                    : { background: "#fff", color: "#1a1a1a", border: "1px solid #e5e7eb" }
                }
              >
                {showBreakdown
                  ? t("dashboard.breakdown.hide", { defaultValue: "Masquer" })
                  : t("dashboard.breakdown.show", { defaultValue: "Analyser ma collection" })}
              </button>
            </div>

            {showBreakdown && (
              <ErrorBoundary message={t("dashboard.errors.module")}>
                <CollectionBreakdown userId={isDelegated ? delegatedCtx?.owner_id : user?.id} />
              </ErrorBoundary>
            )}
          </div>
        )}

        {/* Oeuvres en vente */}
        {OeuvresEnVente && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10">
            <ErrorBoundary message={t("dashboard.errors.module")}>
              <OeuvresEnVente onUpdate={fetchCollections} />
            </ErrorBoundary>
          </div>
        )}

        </>)}
        <div id="sec-marche" className="scroll-mt-24" />
        {showSec("sec-marche") && (<>
        {!isDelegated && (
          <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-10">
            <div className="px-6 py-7 md:px-8 md:py-9 border-b border-gray-100">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-[0.18em] font-medium text-gray-500">
                      {t("dashboard.market.eyebrow", { defaultValue: "Votre veille personnalisée" })}
                    </span>
                    <span className="rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-wide font-medium text-amber-800">
                      {t("dashboard.market.preview", { defaultValue: "Aperçu interface" })}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-light text-gray-950 mb-3" style={{ fontFamily: "Georgia, serif" }}>
                    {t("dashboard.market.title", { defaultValue: "Marché & actualités" })}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    {t("dashboard.market.subtitle", {
                      defaultValue: "Les ventes, expositions et informations qui concernent réellement les artistes de votre collection.",
                    })}
                  </p>
                </div>
                <button type="button" onClick={() => setMarketPreviewTab("followed")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 self-start">
                  {t("dashboard.market.preferences", { defaultValue: "Gérer mes suivis" })}
                  <span aria-hidden="true">→</span>
                </button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-8">
                {[
                  { value: marketPreview.stats.watchedArtists, label: t("dashboard.market.stats.watched", { defaultValue: "Artistes surveillés" }), detail: t("dashboard.market.stats.watchedDetail", { defaultValue: "Collection + suivis" }) },
                  { value: marketPreview.stats.matchingAuctions, label: t("dashboard.market.stats.auctions", { defaultValue: "Ventes correspondantes" }), detail: t("dashboard.market.stats.auctionsDetail", { defaultValue: "À venir" }) },
                  { value: marketPreview.stats.unreadNews, label: t("dashboard.market.stats.unread", { defaultValue: "Actualités non lues" }), detail: t("dashboard.market.stats.unreadDetail", { defaultValue: "À découvrir" }) },
                  { value: marketPreview.stats.upcomingEvents, label: t("dashboard.market.stats.events", { defaultValue: "Événement à venir" }), detail: t("dashboard.market.stats.eventsDetail", { defaultValue: "Foires & expositions" }) },
                ].map((item) => (
                  <button key={item.label} type="button" className="text-left rounded-xl border border-[#e7dcc0] bg-[#fbf8f0] p-4 md:p-5 hover:border-[#c9a961] hover:shadow-sm transition-all">
                    <div className="text-3xl md:text-4xl font-light text-gray-950">{item.value}</div>
                    <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-[#806e43]">{item.label}</div>
                    <div className="mt-1 text-xs text-gray-500">{item.detail}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 md:px-8 border-b border-gray-200 overflow-x-auto">
              <div className="flex min-w-max gap-7">
                {[
                  { id: "auctions", label: t("dashboard.market.tabs.auctions", { defaultValue: "Ventes à venir" }), count: marketPreview.stats.matchingAuctions },
                  { id: "news", label: t("dashboard.market.tabs.news", { defaultValue: "Actualités" }), count: marketPreview.stats.unreadNews },
                  { id: "agenda", label: t("dashboard.market.tabs.agenda", { defaultValue: "Agenda" }), count: marketPreview.stats.upcomingEvents },
                  { id: "followed", label: t("dashboard.market.tabs.followed", { defaultValue: "Mes suivis" }), count: marketPreview.stats.watchedArtists },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setMarketPreviewTab(tab.id)}
                    className={`py-4 text-sm font-medium border-b-2 transition-colors ${marketPreviewTab === tab.id ? "border-black text-black" : "border-transparent text-gray-500 hover:text-gray-900"}`}
                  >
                    {tab.label}
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${marketPreviewTab === tab.id ? "bg-black text-white" : "bg-gray-100 text-gray-600"}`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 md:p-8">
              {marketPreviewTab === "auctions" && (
                <div>
                  <div className="flex items-end justify-between gap-4 mb-5">
                    <div>
                      <h3 className="text-xl font-medium text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
                        {t("dashboard.market.auctions.title", { defaultValue: "Ventes à venir pour votre collection" })}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">{t("dashboard.market.auctions.subtitle", { defaultValue: "Sélectionnées à partir de vos artistes surveillés." })}</p>
                    </div>
                    <button type="button" className="hidden sm:block text-sm font-medium text-gray-700 hover:text-black">
                      {t("dashboard.market.viewAll", { defaultValue: "Tout voir" })} →
                    </button>
                  </div>
                  <div className="grid lg:grid-cols-2 gap-4">
                    {marketPreview.auctions.map((auction) => (
                      <article key={auction.id} className="rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-sm transition-all">
                        <div className="flex items-start justify-between gap-4">
                          <span className="inline-flex rounded-full bg-[#f7f1e3] px-3 py-1 text-xs font-medium text-[#7b683d]">{auction.reason}</span>
                          <span className="text-xs text-gray-400">{auction.date}</span>
                        </div>
                        <h4 className="mt-5 text-lg font-medium text-gray-950">{auction.artist}</h4>
                        <p className="mt-1 text-sm text-gray-700">{auction.title}</p>
                        <p className="mt-3 text-sm text-gray-500">{auction.house} · {auction.place}</p>
                        <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-4">
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">{t("dashboard.market.estimate", { defaultValue: "Estimation" })}</div>
                            <div className="text-sm font-medium text-gray-900 mt-1">{auction.estimate}</div>
                          </div>
                          {auction.url ? (
                            <a href={auction.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-800 hover:text-black">{t("dashboard.market.details", { defaultValue: "Voir le détail" })} →</a>
                          ) : (
                            <span className="text-sm font-medium text-gray-400">{t("dashboard.market.details", { defaultValue: "Voir le détail" })} →</span>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                  {marketPreview.auctions.length === 0 && (
                    <p className="text-sm text-gray-500 py-10 text-center">
                      {t("dashboard.market.auctions.empty", { defaultValue: "Aucune vente correspondant à vos artistes n'a été détectée pour le moment. ARTKIV continue de surveiller le marché." })}
                    </p>
                  )}
                </div>
              )}

              {marketPreviewTab === "news" && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                    <div>
                      <h3 className="text-xl font-medium text-gray-950" style={{ fontFamily: "Georgia, serif" }}>{t("dashboard.market.news.title", { defaultValue: "Actualités de vos artistes" })}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t("dashboard.market.news.subtitle", { defaultValue: "Galeries partenaires et marché." })}</p>
                    </div>
                    <div className="flex gap-2">
                      {["Tout", "Galeries", "Marché"].map((filter, index) => <button key={filter} type="button" className={`rounded-full px-3 py-1.5 text-xs border ${index === 0 ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-200"}`}>{filter}</button>)}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 border-y border-gray-100">
                    {marketPreview.news.map((item) => (
                      <article key={item.id} className="py-5 flex gap-4">
                        <div className="mt-2 h-2 w-2 rounded-full bg-[#c9a961] flex-none" aria-label={item.unread ? "Non lu" : ""} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500"><span className="font-medium text-gray-800">{item.source}</span><span>·</span><span>{item.type}</span></div>
                          <h4 className="mt-2 text-base font-medium text-gray-950">{item.title}</h4>
                          <p className="mt-1 text-sm text-gray-500">{item.meta}</p>
                          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <span className="text-xs text-[#806e43]">{t("dashboard.market.why", { defaultValue: "Pourquoi vous le voyez" })} : {item.reason}</span>
                            {item.url ? (
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="self-start text-sm font-medium text-gray-800 hover:text-black">{t("dashboard.market.read", { defaultValue: "Lire l’actualité" })} →</a>
                            ) : (
                              <span className="self-start text-sm font-medium text-gray-400">{t("dashboard.market.read", { defaultValue: "Lire l’actualité" })} →</span>
                            )}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                  {marketPreview.news.length === 0 && (
                    <p className="text-sm text-gray-500 py-10 text-center">
                      {t("dashboard.market.news.empty", { defaultValue: "Aucune actualité pour vos artistes pour le moment. ARTKIV continue de surveiller la presse spécialisée." })}
                    </p>
                  )}
                </div>
              )}

              {marketPreviewTab === "agenda" && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                    <div>
                      <h3 className="text-xl font-medium text-gray-950" style={{ fontFamily: "Georgia, serif" }}>{t("dashboard.market.agenda.title", { defaultValue: "Foires & expositions" })}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t("dashboard.market.agenda.subtitle", { defaultValue: "Événements à venir ou en cours, selon vos centres d'intérêt et les rendez-vous majeurs du marché." })}</p>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 border-y border-gray-100">
                    {marketPreview.events.map((item) => (
                      <article key={item.id} className="py-5 flex gap-4">
                        <div className="mt-2 h-2 w-2 rounded-full bg-[#c9a961] flex-none" aria-label={item.unread ? "Non lu" : ""} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500"><span className="font-medium text-gray-800">{item.source}</span><span>·</span><span>{item.type}</span></div>
                          <h4 className="mt-2 text-base font-medium text-gray-950">{item.title}</h4>
                          <p className="mt-1 text-sm text-gray-500">{item.meta}</p>
                          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <span className="text-xs text-[#806e43]">{t("dashboard.market.why", { defaultValue: "Pourquoi vous le voyez" })} : {item.reason}</span>
                            {item.url ? (
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="self-start text-sm font-medium text-gray-800 hover:text-black">{t("dashboard.market.details", { defaultValue: "Voir le détail" })} →</a>
                            ) : (
                              <span className="self-start text-sm font-medium text-gray-400">{t("dashboard.market.details", { defaultValue: "Voir le détail" })} →</span>
                            )}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                  {marketPreview.events.length === 0 && (
                    <p className="text-sm text-gray-500 py-10 text-center">
                      {t("dashboard.market.agenda.empty", { defaultValue: "Aucun événement correspondant n'est actuellement référencé. Vos suivis restent actifs." })}
                    </p>
                  )}
                </div>
              )}

              {marketPreviewTab === "followed" && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                    <div>
                      <h3 className="text-xl font-medium text-gray-950" style={{ fontFamily: "Georgia, serif" }}>{t("dashboard.market.followed.title", { defaultValue: "Artistes surveillés" })}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t("dashboard.market.followed.subtitle", { defaultValue: "Les artistes de votre collection sont surveillés automatiquement. Ajoutez une priorité pour renforcer le suivi." })}</p>
                    </div>
                    {!isDelegated && (
                      <button type="button" onClick={addFollow} className="rounded-xl bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-gray-900">+ {t("dashboard.market.followed.add", { defaultValue: "Suivre un artiste" })}</button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {marketPreview.artists.map((artist) => (
                      <div key={artist.id} className="rounded-xl border border-gray-200 px-4 py-4 flex items-center justify-between gap-4">
                        <div>
                          <div className="font-medium text-gray-950">{artist.name}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {artist.collection && <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-gray-600">{t("dashboard.market.followed.inCollection", { defaultValue: "Dans ma collection" })}</span>}
                            {artist.priority && <span className="rounded-full bg-[#f7f1e3] px-2.5 py-1 text-[11px] text-[#7b683d]">{t("dashboard.market.followed.priority", { defaultValue: "Suivi en priorité" })}</span>}
                          </div>
                        </div>
                        {!isDelegated && (
                          <button
                            type="button"
                            onClick={() => toggleFollow(artist)}
                            className="text-xs rounded-full border px-3 py-1.5 whitespace-nowrap transition-colors"
                            style={artist.priority ? { borderColor: "#c9a961", color: "#a9843f", background: "#faf6ec" } : { borderColor: "#e5e0d8", color: "#6b6459", background: "#fff" }}
                          >
                            {artist.priority
                              ? t("dashboard.market.followed.unprioritize", { defaultValue: "Retirer la priorité" })
                              : t("dashboard.market.followed.prioritize", { defaultValue: "Suivre en priorité" })}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {marketPreview.artists.length === 0 && (
                    <p className="text-sm text-gray-500 py-10 text-center">
                      {t("dashboard.market.followed.empty", { defaultValue: "Aucun artiste surveillé pour le moment. Ajoutez des œuvres à votre collection ou suivez un artiste." })}
                    </p>
                  )}
                  <div className="mt-5 rounded-xl bg-gray-50 px-4 py-3 text-xs text-gray-500">
                    {t("dashboard.market.followed.note", { defaultValue: "Aucun artiste n’est compté deux fois : le total réunit votre collection et vos suivis manuels." })}
                  </div>

                  <div className="mt-10 pt-8 border-t border-gray-100">
                    <h3 className="text-xl font-medium text-gray-950" style={{ fontFamily: "Georgia, serif" }}>{t("dashboard.market.interests.title", { defaultValue: "Centres d'intérêt" })}</h3>
                    <p className="text-sm text-gray-500 mt-1">{t("dashboard.market.interests.subtitle", { defaultValue: "Complète votre veille avec des foires et expositions au-delà de vos artistes surveillés." })}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {marketPreview.interests.map((interest) => (
                        <button
                          key={interest.id}
                          type="button"
                          disabled={isDelegated}
                          onClick={() => toggleInterest(interest)}
                          className="rounded-full border px-3 py-1.5 text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed"
                          style={interest.selected ? { borderColor: "#c9a961", color: "#a9843f", background: "#faf6ec" } : { borderColor: "#e5e0d8", color: "#6b6459", background: "#fff" }}
                        >
                          {interest.label}
                        </button>
                      ))}
                    </div>
                    <label className="mt-5 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={marketPreview.showMajorEvents}
                        disabled={isDelegated}
                        onChange={toggleShowMajorEvents}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      {t("dashboard.market.interests.showMajor", { defaultValue: "Afficher aussi les grands rendez-vous du marché (Art Basel, Frieze…) même sans lien direct avec mes artistes" })}
                    </label>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        </>)}
        <div id="sec-propositions" className="scroll-mt-24" />
        {showSec("sec-propositions") && (<>
        {/* Actions — agir sur la collection */}
        {ActualitesPropositions && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10">
            <h2 className="text-2xl font-light text-gray-900 mb-2" style={{ fontFamily: "Georgia, serif" }}>
              {t("dashboard.actions.title", { defaultValue: "Actions" })}
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              {t("dashboard.actions.subtitle", {
                defaultValue:
                  "Protect, assess, share and prepare what happens next for your artworks.",
              })}
            </p>

            {/* PROTÉGER */}
            <ActionEyebrow>{t("dashboard.actions.protect", { defaultValue: "Protect" })}</ActionEyebrow>

            {/* Assurance */}
            <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5 mb-3">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <IconChip icon={Shield} tone="ink" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {t("dashboard.insurance.summary.title")}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{t("dashboard.insurance.summary.desc")}</div>

                    {insSummaryLoading ? (
                      <div className="text-xs text-gray-500 mt-2">{t("common.loading.safe")}</div>
                    ) : insSummaryError ? (
                      <div className="text-xs text-red-600 mt-2">{insSummaryError}</div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-xs px-2 py-1 rounded-lg border border-[#e8e3d8] bg-[#faf9f7]">
                          {t("dashboard.insurance.summary.total")}: <b>{insSummary.total}</b>
                        </span>
                        <span className="text-xs px-2 py-1 rounded-lg border border-[#e8e3d8] bg-[#faf9f7]">
                          {t("dashboard.insurance.summary.pending")}: <b>{insSummary.pending}</b>
                        </span>
                        <span className="text-xs px-2 py-1 rounded-lg border border-[#e8e3d8] bg-[#faf9f7]">
                          {t("dashboard.insurance.summary.seen")}: <b>{insSummary.seen}</b>
                        </span>
                        <span className="text-xs px-2 py-1 rounded-lg border border-[#e8e3d8] bg-[#faf9f7]">
                          {t("dashboard.insurance.summary.answered")}: <b>{insSummary.answered}</b>
                        </span>
                        {insSummary.latest ? (
                          <span className="text-xs px-2 py-1 rounded-lg border border-[#e8e3d8] bg-[#faf9f7]">
                            {t("dashboard.insurance.summary.latestStatus")}:{" "}
                            <b>
                              {t(`insurance.status.${insSummary.latest.status}`, {
                                defaultValue: insSummary.latest.status,
                              })}
                            </b>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-row sm:flex-col items-stretch sm:items-end gap-2 shrink-0">
                  <Link
                    to="/insurance/requests"
                    className="text-center px-3 py-2 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900"
                  >
                    {t("dashboard.insurance.summary.ctaFollow")}
                  </Link>
                  <Link
                    to="/insurance/request"
                    className="text-center px-3 py-2 rounded-xl text-sm font-medium bg-white border border-[#e8e3d8] hover:bg-gray-50"
                  >
                    {t("dashboard.insurance.summary.ctaNew")}
                  </Link>
                </div>
              </div>
            </div>

            {/* Pertes & vols — carte d'alerte (accent rouge à gauche, sans fond plein) */}
            <div
              className="rounded-2xl border border-[#e8e3d8] bg-white p-5 mb-8 flex items-center justify-between gap-4"
              style={{ borderLeft: "3px solid #dc2626" }}
            >
              <div className="flex items-start gap-3">
                <IconChip icon={AlertTriangle} tone="red" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t("dashboard.losses.title")}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{t("dashboard.losses.description")}</p>
                </div>
              </div>
              <Link
                to="/pertes-vols"
                className="shrink-0 px-3 py-2 rounded-xl text-xs font-medium text-white whitespace-nowrap"
                style={{ background: "#b3261e" }}
              >
                {t("dashboard.losses.cta")}
              </Link>
            </div>

            {/* EXPERTISER */}
            <ActionEyebrow>{t("dashboard.actions.assess", { defaultValue: "Assess" })}</ActionEyebrow>
            <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5 mb-8">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-3">
                  <IconChip icon={Search} tone="ink" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t("dashboard.actions.expertise.title", {
                        defaultValue: "Expertise & valuation",
                      })}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      {t("dashboard.actions.expertise.desc", {
                        defaultValue:
                          "Find a professional when an artwork needs expertise, valuation or specialist advice.",
                      })}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={goFindExpert}
                  className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900"
                >
                  {t("dashboard.access.ctaFindExpert", {
                    defaultValue: "Find an expert",
                  })}
                </button>
              </div>

              <div
                className="mt-4 p-4 rounded-xl"
                style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: "#c9a961", color: "#1a1a1a" }}
                    aria-hidden="true"
                  >
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold mb-1" style={{ color: "#1a1a1a" }}>
                      {t("dashboard.access.freeConnection.title")}
                    </div>
                    <div className="text-xs leading-relaxed mb-2" style={{ color: "#6b6459" }}>
                      {t("dashboard.access.freeConnection.description")}
                    </div>
                    <div
                      className="text-xs rounded px-2 py-1.5"
                      style={{
                        color: "#7a5f1a",
                        background: "#fff",
                        border: "1px solid #e6d9b3",
                      }}
                    >
                      <strong>{t("dashboard.access.freeConnection.paidLabel")}</strong>{" "}
                      {t("dashboard.access.freeConnection.paidText")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CERTIFIER — authenticité & empreinte numérique */}
            <ActionEyebrow>{t("dashboard.actions.certify", { defaultValue: "Certify" })}</ActionEyebrow>
            <div className="space-y-3 mb-8">
              <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-start gap-3">
                    <IconChip icon={BadgeCheck} tone="ink" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {t("dashboard.actions.authenticity.title", {
                          defaultValue: "Authenticity review",
                        })}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-600">
                        {t("dashboard.actions.authenticity.desc", {
                          defaultValue:
                            "Submit the artwork and its supporting documents for a human expert review.",
                        })}
                      </p>
                      <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
                        {t("dashboard.actions.authenticity.note", {
                          defaultValue:
                            "Submitting a dossier does not itself constitute a certificate of authenticity.",
                        })}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/authenticity-request"
                    className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900"
                  >
                    {t("dashboard.actions.authenticity.cta", {
                      defaultValue: "Submit a dossier",
                    })}
                  </Link>
                </div>
              </div>

              <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-start gap-3">
                    <IconChip icon={Fingerprint} tone="ink" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {t("dashboard.actions.fingerprint.title", {
                          defaultValue: "Digital fingerprint",
                        })}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-600">
                        {t("dashboard.actions.fingerprint.desc", {
                          defaultValue:
                            "Create a dated SHA-256 fingerprint of an artwork record to verify the integrity of its recorded data.",
                        })}
                      </p>
                      <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
                        {t("dashboard.actions.fingerprint.note", {
                          defaultValue:
                            "Current phase: ARTKIV hash. Public Tezos anchoring is not yet active.",
                        })}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/blockchain-demo"
                    className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium bg-white border border-[#e8e3d8] hover:bg-gray-50"
                  >
                    {t("dashboard.actions.fingerprint.cta", {
                      defaultValue: "View digital fingerprint",
                    })}
                  </Link>
                </div>
              </div>
            </div>

            {/* OPPORTUNITÉS INSTITUTIONNELLES */}
            <ActionEyebrow>
              {t("dashboard.actions.opportunities", { defaultValue: "Institutional opportunities" })}
            </ActionEyebrow>
            <div className="mb-8">
              <ErrorBoundary message={t("dashboard.errors.module")}>
                <ActualitesPropositions />
              </ErrorBoundary>
            </div>

            {/* PRÉPARER LA SUITE */}
            <ActionEyebrow>{t("dashboard.actions.future", { defaultValue: "Prepare the future" })}</ActionEyebrow>
            <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-3">
                  <IconChip icon={ScrollText} tone="ink" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t("dashboard.actions.succession.title", {
                        defaultValue: "Transmission & succession",
                      })}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      {t("dashboard.actions.succession.desc", {
                        defaultValue:
                          "Prepare beneficiaries, ownership situations and private scenarios for the future of your collection.",
                      })}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
                      {t("dashboard.actions.succession.note", {
                        defaultValue:
                          "ARTKIV provides a preparation and simulation framework, not legal or tax advice.",
                      })}
                    </p>
                  </div>
                </div>
                <Link
                  to="/succession"
                  className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900"
                >
                  {t("dashboard.actions.succession.cta", {
                    defaultValue: "Prepare transmission",
                  })}
                </Link>
              </div>
            </div>
          </div>
        )}
        </>)}

        {/* Modal de partage */}
        {ShareAccessModal && shareOpen && shareTargetId && (
          <ErrorBoundary message={t("dashboard.errors.module")}>
            <ShareAccessModal
              open={shareOpen}
              onClose={() => setShareOpen(false)}
              collectionId={shareTargetId}
              collectionTitle={activeCollectionTitle}
            />
          </ErrorBoundary>
        )}
        </div>
      </div>
    </div>
  );
};

export { Dashboard };
export default Dashboard;
